import {
  Component,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  getFullLocaleCode,
  initializeI18n,
  stripLanguagePrefix,
  useTranslation,
} from "@gsb-core/react-multilang";
import { AuthService } from "@gsb-core/core";
import { AuthProvider, useSession } from "@gsb-core/react-auth";
import { Button, ConfirmProvider, Toaster } from "@gsb-core/react-components";
import { DataTablePreferencesProvider } from "@gsb-core/react-data-table";
import {
  CommandRegistryProvider,
  PageState,
  Spinner,
  TestFlightBanner,
  ThemeProvider,
} from "@gsb-core/react-ui";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { createWorkspaceApp, workspaceRouteManifest } from "./gsb-app";
import { authClient } from "./auth-client";
import { WorkspaceAppShell } from "./components/workspace-app-shell";
import NotFound from "./not-found";
import { PublicRouteMetadata } from "./public-metadata";
import { AssistantSurfaceContextProvider } from "./assistant-surface-context";
import { LazyLegalCookieConsent, LegalProvider } from "./legal-consent";
import { protectedRouteLoginPath } from "./language-path";
import { WorkspaceNavigationBoundary } from "./workspace-navigation-boundary";

class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  override state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
    this.setState({ hasError: true });
  }

  override render() {
    if (this.state.hasError) {
      return (
        <PageState
          icon={AlertTriangle}
          eyebrow="Application error"
          title="Something went wrong"
          description="This page could not be displayed. Reload it to try again."
          actions={
            <Button type="button" onClick={() => window.location.reload()}>
              <RefreshCw aria-hidden className="me-2 h-4 w-4" />
              Reload page
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient();

function RouteLoading() {
  return (
    <div
      className="flex min-h-[16rem] items-center justify-center p-8 text-content-tertiary"
      role="status"
      aria-live="polite"
    >
      <Spinner size="lg" label="Loading page" />
    </div>
  );
}

function ProtectedRoute() {
  const session = useSession();
  const { user } = session;
  const location = useLocation();
  const [authReady, setAuthReady] = useState(false);
  const refreshStarted = useRef(false);
  const refreshSession = useCallback(() => session.refresh(), [session]);

  useEffect(() => {
    if (refreshStarted.current) return;
    refreshStarted.current = true;
    void refreshSession().finally(() => setAuthReady(true));
  }, [refreshSession]);

  if (!authReady) return <RouteLoading />;
  const hasValidAuth =
    user !== null && AuthService.getInstance().isAuthenticated();
  if (!hasValidAuth) {
    return (
      <Navigate
        to={protectedRouteLoginPath(
          location.pathname,
          location.search,
          location.hash,
        )}
        replace
      />
    );
  }
  return (
    <DataTablePreferencesProvider userId={user.id}>
      <Outlet />
    </DataTablePreferencesProvider>
  );
}

function localizedPath(path: string): string {
  return path === "/" ? "/:language" : `/:language${path}`;
}

function routePaths(path: string): string[] {
  return [path, localizedPath(path)];
}

function LanguageRouteBoundary() {
  const { language } = useParams();
  const { language: activeLanguage, changeLanguage } = useTranslation();
  const locale = getFullLocaleCode(language ?? "en");
  useEffect(() => {
    if (activeLanguage !== locale) void changeLanguage(locale);
  }, [activeLanguage, changeLanguage, locale]);
  if (language && stripLanguagePrefix(`/${language}`) === `/${language}`) {
    return <NotFound />;
  }
  return <Outlet />;
}

function DashboardNotFoundRoute() {
  return <NotFound />;
}

function WorkspaceClientRoot() {
  const [router] = useState(() =>
    createBrowserRouter([{ path: "*", element: <WorkspaceRouterRoot /> }]),
  );
  return (
    <AppErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <CommandRegistryProvider>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </CommandRegistryProvider>
        </ThemeProvider>
      </HelmetProvider>
    </AppErrorBoundary>
  );
}

function WorkspaceRouterRoot() {
  const publicRoutes = workspaceRouteManifest.filter(
    (route) => route.shell === "public",
  );
  const dashboardRoutes = workspaceRouteManifest.filter(
    (route) => route.shell === "dashboard",
  );
  return (
    <WorkspaceNavigationBoundary>
      <LegalProvider>
        <AuthProvider client={authClient}>
          <ConfirmProvider>
            <TestFlightBanner />
            <AssistantSurfaceContextProvider>
              <Suspense fallback={<RouteLoading />}>
                <Routes>
                  <Route element={<LanguageRouteBoundary />}>
                    {publicRoutes.flatMap(({ path, component: Component }) =>
                      routePaths(path).map((routePath) => (
                        <Route
                          key={routePath}
                          path={routePath}
                          element={
                            <>
                              <PublicRouteMetadata
                                manifest={workspaceRouteManifest}
                              />
                              <Component />
                            </>
                          }
                        />
                      )),
                    )}
                    <Route element={<ProtectedRoute />}>
                      <Route
                        element={
                          <WorkspaceAppShell
                            shell="dashboard"
                            manifest={workspaceRouteManifest}
                          >
                            <Suspense fallback={<RouteLoading />}>
                              <Outlet />
                            </Suspense>
                          </WorkspaceAppShell>
                        }
                      >
                        {dashboardRoutes.flatMap(
                          ({ path, component: Component }) =>
                            routePaths(path).map((routePath) => (
                              <Route
                                key={routePath}
                                path={routePath}
                                element={<Component />}
                              />
                            )),
                        )}
                        <Route
                          path="/dashboard/*"
                          element={<DashboardNotFoundRoute />}
                        />
                        <Route
                          path="/:language/dashboard/*"
                          element={<DashboardNotFoundRoute />}
                        />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AssistantSurfaceContextProvider>
            <Toaster />
            <LazyLegalCookieConsent />
          </ConfirmProvider>
        </AuthProvider>
      </LegalProvider>
    </WorkspaceNavigationBoundary>
  );
}

export const app = createWorkspaceApp({
  env: {
    get: (name) => {
      const value: unknown = import.meta.env[name];
      return typeof value === "string" ? value : undefined;
    },
    isDevelopment: import.meta.env.DEV,
  },
  logger: {
    debug: () => undefined,
    info: () => undefined,
    warn: () => undefined,
    error: () => undefined,
  },
  navigation: {
    href: (path) => path,
    navigate: (path, options) => {
      if (options?.replace) window.location.replace(path);
      else window.location.assign(path);
    },
  },
  client: {
    createRoot: (context) => {
      initializeI18n({
        debug: context.env.isDevelopment,
        backend: { loadPath: "/locales/{{lng}}/{{ns}}.json" },
      });
      return WorkspaceClientRoot;
    },
  },
});

export const ClientRoot = app.clientRoot;
